/**
 * Chat Context
 * React state management for chat system
 * Wraps the existing ChatHandler class
 */

import React, { createContext, useContext, useCallback, useState, useEffect, ReactNode } from 'react';
import { ChatHandler } from '../../handlers/chat/ChatHandler';
import { ChatMessage } from '../HUD/ChatTypes';
import { getChatFilter } from '../../../config/ui/chat.config';
import { getDevConfig } from '../../../config/environment/devConfig';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { gameLogger } from '../../../utils/GameLogger';
import { generateWelcomeMap } from '../../../shared/builders/WelcomeMapGenerator';
import { generateTutorialMap } from '../../../shared/builders/TutorialMapGenerator';
import { LevelFormatConverter } from '../../../shared/LevelFormatConverter';
import { LevelRegistry } from '../../systems/map/LevelRegistry';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { pageLifetimeContext } from './pageLifetimeContext';
import { matchSpawnCommand, spawnMobCommand } from '../../services/devChatCommands';
import { quickKeybindStore } from '../Systems/ActionBar/quickKeybindStore';

interface ChatContextState {
  /** All chat messages */
  messages: ChatMessage[];
  /** Currently visible messages (filtered) */
  visibleMessages: ChatMessage[];
  /** Current filter ID */
  filter: string;
  /** Chat input focused state */
  isFocused: boolean;
  /** Chat auto-scroll paused */
  isPaused: boolean;
  /** Chat collapsed */
  isCollapsed: boolean;
}

interface ChatContextActions {
  /** Send a chat message */
  sendMessage: (text: string, type?: ChatMessage['type']) => void;
  /** Add a message to chat */
  addMessage: (message: ChatMessage) => void;
  /** Clear all messages */
  clearMessages: () => void;
  /** Set message filter */
  setFilter: (filter: string) => void;
  /** Toggle pause state */
  togglePause: () => void;
  /** Set focus state */
  setFocused: (focused: boolean) => void;
  /** Toggle collapsed */
  toggleCollapsed: () => void;
  /** Focus the input */
  focusInput: () => void;
}

type ChatContextType = ChatContextState & ChatContextActions;

// Export singleton instance for non-React access
export const chatHandler = ChatHandler.getInstance();

// Vite can replace a context module and its consumers in separate HMR frames.
// Recreating the Context object in that interval leaves the old Provider and
// new hook connected to different identities, which trips the guard below and
// strands GameUIOverlay inside its error boundary. Keep the identity stable
// for the life of the page; a real reload still starts with a clean registry.
const ChatContext = pageLifetimeContext(
  'chat',
  () => createContext<ChatContextType | null>(null),
);

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  // Use singleton ChatHandler instance
  const chatHandler = ChatHandler.getInstance();

  // State
  const [messages, setMessages] = useState<ChatMessage[]>(chatHandler.getMessages());
  const [filter, setFilterState] = useState<string>('all');
  const [isFocused, setIsFocused] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Initial collapsed state honors devConfig.presentation.chatDefaultExpanded.
  // true (default) = chat opens immediately so combat-log + system events
  // are visible without a click. Flip per-preset by overriding in
  // PRESET_PRESENTATION.<preset>.chatDefaultExpanded = false.
  const [isCollapsed, setIsCollapsed] = useState(!getDevConfig().presentation.chatDefaultExpanded);

  // Subscribe to ChatHandler updates
  useEffect(() => {
    const unsubscribe = chatHandler.subscribe((newMessages) => {
      if (!isPaused) {
        setMessages(newMessages);
      }
    });
    return unsubscribe;
  }, [chatHandler, isPaused]);

  // Enter key (from InputSystem → SceneInputRouter → EventBus) opens + focuses chat
  useEffect(() => {
    const unsub = EventBus.getInstance().on(EVENTS.UI_ACTION, ({ action }: any) => {
      if (action === 'open_chat') {
        setIsCollapsed(false);
        setIsFocused(true);
      }
    });
    return () => unsub();
  }, []);

  // Calculate visible messages based on filter
  const visibleMessages = messages.filter(msg => {
    const filterConfig = getChatFilter(filter);
    return filterConfig.types.includes(msg.type);
  });

  // Actions
  const sendMessage = useCallback((text: string, type: ChatMessage['type'] = 'global') => {
    const trimmed = text.trim();

    // ElvUI-style Quick Keybind Mode: /kb or /keybind
    if (/^\/(kb|keybind)$/i.test(trimmed)) {
      const enabled = quickKeybindStore.toggleQuickKeybindMode();
      ChatHandler.getInstance().systemMessage(
        enabled
          ? 'Quick Keybind Mode enabled — hover any action bar slot and press a key (or Shift/Ctrl/Alt + key) to bind. Press Esc or type /kb to finish.'
          : 'Quick Keybind Mode closed.',
      );
      setIsFocused(false);
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      return;
    }

    // Intercept development commands
    // Add hook for /genmap
    if (trimmed.startsWith('/genmap')) {
      const parts = text.trim().split(' ');
      const mapType = parts[1] || 'welcome';

      try {
        let gridLevel;
        if (mapType === 'tutorial') {
          gridLevel = generateTutorialMap();
          ChatHandler.getInstance().systemMessage('Generating Tutorial Sandbox Map...');
        } else {
          gridLevel = generateWelcomeMap();
          ChatHandler.getInstance().systemMessage('Generating Welcome Map...');
        }

        // Convert to runtime format
        const platformerLevel = LevelFormatConverter.gridToPlatformer(gridLevel);

        // Register globally
        LevelRegistry.getInstance().registerLevel(platformerLevel);

        // Command the engine to switch scenes
        // (Assuming MapSystem handles this, we dispatch an event for now,
        // or just let the user know it's ready. In the future this should trigger an actual teleport)
        ChatHandler.getInstance().systemMessage(`Map '${gridLevel.name}' registered! Use /teleport ${gridLevel.id} to visit.`);

      } catch (err: any) {
        ChatHandler.getInstance().errorMessage(`Failed to generate map: ${err.message}`);
      }
      return;
    }

    // /spawnmob <type> [x] [y] (and its alias /spawn) — debug command for adding
    // a mob to the live room state. Used by the editor preset for the "blank
    // canvas → add pig_mom → fight her" QA flow. If no coords are given, the
    // server plants the mob ~80px to the right of the player. Answers in chat.
    const spawnArgs = matchSpawnCommand(text);
    if (spawnArgs !== null) {
      spawnMobCommand(spawnArgs, ChatHandler.getInstance());
      return;
    }

    // Chat channel shortcuts: /w, /p, /g, /s
    const channelMatch = text.match(/^\/(w|whisper|p|party|g|guild|s|say)\s+(.+)/i);
    if (channelMatch) {
      const channelMap: Record<string, string> = {
        w: 'whisper', whisper: 'whisper',
        p: 'party', party: 'party',
        g: 'guild', guild: 'guild',
        s: 'say', say: 'say',
      };
      const channel = channelMap[channelMatch[1].toLowerCase()] || 'global';
      let message = channelMatch[2];
      let targetName: string | undefined;

      // Whisper: /w <name> <message>
      if (channel === 'whisper') {
        const whisperParts = message.match(/^(\S+)\s+(.*)/);
        if (whisperParts) {
          targetName = whisperParts[1];
          message = whisperParts[2];
        }
      }

      // Send to server with channel info
      try {
        const registry = ServiceRegistry.getInstance();
        if (registry.has('NetworkManager')) {
          const nm = registry.get('NetworkManager');
          nm?.getRoom?.()?.send('chatMessage', { message, channel, targetName });
        }
      } catch { /* non-critical */ }
      return;
    }

    // Route all other slash commands to the central processor in Game.tsx
    if (text.startsWith('/')) {
      EventBus.getInstance().emit(EVENTS.CHAT_COMMAND, { command: text });
      return;
    }

    // Send via server for multiplayer sync
    try {
      const registry = ServiceRegistry.getInstance();
      if (registry.has('NetworkManager')) {
        const nm = registry.get('NetworkManager');
        const room = nm?.getRoom?.();
        if (room) {
          room.send('chatMessage', { message: text, channel: 'global' });
          return;
        }
      }
    } catch { /* fallback to local */ }

    // Fallback: local-only chat (offline mode)
    chatHandler.sendMessage(text, 'Player', type as any);
  }, [chatHandler]);

  const addMessage = useCallback((message: ChatMessage) => {
    chatHandler.addMessage(message);
  }, [chatHandler]);

  const clearMessages = useCallback(() => {
    chatHandler.clear();
    gameLogger.info('chat', 'Chat cleared');
  }, [chatHandler]);

  const setFilter = useCallback((newFilter: string) => {
    setFilterState(newFilter);
    gameLogger.debug('chat', `Filter changed to: ${newFilter}`);
  }, []);

  const togglePause = useCallback(() => {
    setIsPaused(prev => {
      const newState = !prev;
      gameLogger.info('chat', `Chat ${newState ? 'paused' : 'resumed'}`);
      return newState;
    });
  }, []);

  const setFocused = useCallback((focused: boolean) => {
    setIsFocused(focused);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const focusInput = useCallback(() => {
    setIsCollapsed(false);
    setIsFocused(true);
  }, []);

  const value: ChatContextType = {
    // State
    messages,
    visibleMessages,
    filter,
    isFocused,
    isPaused,
    isCollapsed,
    // Actions
    sendMessage,
    addMessage,
    clearMessages,
    setFilter,
    togglePause,
    setFocused,
    toggleCollapsed,
    focusInput
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
